import { useCallback, useContext, useEffect, useRef, useState } from "react";
import {
  AuthContext,
  S3CredentialsContext,
  S3CredentialsContextInterface,
} from "../../../contexts";
import { uploadToS3 } from "../../../utils/S3Utils";
import { styles } from "./styles";
import { Props } from "./types";
import { Typography } from "@mui/material";
import Player from "../../../reactronica/components/Player";
import TrackControls from "../TrackControls/TrackControls";
import useDnDTrack from "./useDnDTrack";
import { SongContext } from "../../../reactronica/components/Song";

const ASSETS_URL = process.env.REACT_APP_ASSET_S3_BUCKET_URL!;

const AudioFileTrack: React.FC<Props> = ({
  trackProperties,
  handleRemoveTrack,
  handleTrackFinished,
  onNameFieldFocusChange,
}) => {
  const {
    track,
    handleNameChange,
    handlePanChange,
    handleMuteChange,
    handleSoloChange,
    handleVolumeChange,
    setTrackUrl,
    setPlayerAsLoaded,
    setDuration,
  } = useDnDTrack(trackProperties);
  const drop = useRef<HTMLDivElement | null>(null);
  const [dragging, setDragging] = useState(false);
  const [dropped, setDropped] = useState(false);
  const [progress, setProgress] = useState(-1);
  const { userId } = useContext(AuthContext);
  const { isPlaying, exporting } = useContext(SongContext);

  const onProgress = (progress: number) => setProgress(progress);

  const { awsAccessKeyId, awsSecretAccessKey, awsSessionToken } =
    useContext<S3CredentialsContextInterface>(S3CredentialsContext);

  const onSuccess = useCallback(
    (key: string) => {
      setDropped(true);
      const url = ASSETS_URL + "/" + key;
      setTrackUrl(url);
      console.log(url);
    },
    [setTrackUrl]
  );

  const onDrop = useCallback(
    async (file: File) => {
      if (userId) {
        uploadToS3(
          userId,
          file,
          "sample",
          "audio/mpeg",
          awsAccessKeyId,
          awsSecretAccessKey,
          awsSessionToken,
          onProgress,
          onSuccess,
          () => {}
        );
      } else {
        alert("Login to drag & drop files");
      }
    },
    [userId, awsAccessKeyId, awsSecretAccessKey, awsSessionToken, onSuccess]
  );

  const handleDragEnter = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
  }, []);

  const handleDragOver = useCallback((e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();

      setDragging(false);
      const files = e.dataTransfer?.files;
      if (files) {
        if (files.length > 1) {
          alert("Only one file can be dropped here");
        } else if (files.length === 0) {
          alert("No files dropped");
        } else {
          onDrop(files[0]);
        }
      }
    },
    [onDrop]
  );

  useEffect(() => {
    if (isPlaying || exporting) {
      if (track.url === undefined) {
        handleTrackFinished(track.id, true);
      }
    } else {
      handleTrackFinished(track.id, undefined);
    }
  }, [exporting, handleTrackFinished, isPlaying, track.id, track.url]);

  const evaluateDragZoneMessage = () => {
    if (dropped && !track.isPlayerLoaded) {
      return "Loading...";
    } else if (progress >= 0) {
      return `Uploading file: ${progress}%`;
    } else {
      return "Drag file to upload";
    }
  };

  useEffect(() => {
    if (drop.current) {
      drop.current.addEventListener("dragover", handleDragOver);
      drop.current.addEventListener("drop", handleDrop);
      drop.current.addEventListener("dragenter", handleDragEnter);
      drop.current.addEventListener("dragleave", handleDragLeave);
    }
    return () => {
      if (drop.current) {
        drop.current.removeEventListener("dragover", handleDragOver);
        drop.current.removeEventListener("dragenter", handleDragEnter);
        drop.current.removeEventListener("dragleave", handleDragLeave);
        // eslint-disable-next-line react-hooks/exhaustive-deps
        drop.current.removeEventListener("drop", handleDrop);
      }
    };
  }, [handleDrop, handleDragOver, handleDragEnter, handleDragLeave]);

  return (
    <>
      <TrackControls
        trackProperties={track}
        handleNameChange={handleNameChange}
        handlePanChange={handlePanChange}
        handleMuteChange={handleMuteChange}
        handleSoloChange={handleSoloChange}
        handleVolumeChange={handleVolumeChange}
        handleRemoveTrack={handleRemoveTrack}
        onNameFieldFocusChange={onNameFieldFocusChange}
      />
      <div
        style={{
          ...styles.root,
          backgroundColor: dragging ? "#808080" : "#222222",
          justifyContent: dropped && track.isPlayerLoaded ? "unset" : "center",
          alignItems: dropped && track.isPlayerLoaded ? "unset" : "center",
        }}
        ref={drop}
      >
        {dropped && track.isPlayerLoaded ? (
          <img src="./assets/images/audio-waveform.webp" alt="audio waveform" />
        ) : (
          <Typography sx={{ color: "white" }}>
            {evaluateDragZoneMessage()}
          </Typography>
        )}
        {track.url && (
          <Player
            id={track.id}
            isPlaying={isPlaying}
            url={track.url!}
            volume={track.volume}
            mute={track.mute}
            solo={track.solo}
            pan={track.pan}
            setPlayerAsLoaded={setPlayerAsLoaded}
            handleTrackFinished={handleTrackFinished}
            setDuration={setDuration}
          />
        )}
      </div>
    </>
  );
};

export default AudioFileTrack;
